import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import Select from './Select';


const RecurringTaskModal = ({ onClose, onSave }) => {
  const [frequency, setFrequency] = useState('daily');
  const [endDate, setEndDate] = useState('');
  const [weeklyDays, setWeeklyDays] = useState([]);
  const [monthlyDay, setMonthlyDay] = useState('1');
  const [errors, setErrors] = useState({});

  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const daysOfWeek = [
    { value: 'monday', label: 'Mon' },
    { value: 'tuesday', label: 'Tue' },
    { value: 'wednesday', label: 'Wed' },
    { value: 'thursday', label: 'Thu' },
    { value: 'friday', label: 'Fri' },
    { value: 'saturday', label: 'Sat' },
    { value: 'sunday', label: 'Sun' }
  ];

  const handleToggleDay = (day) => {
    setWeeklyDays(prev => 
      prev?.includes(day)
        ? prev?.filter(d => d !== day)
        : [...prev, day]
    );
  };

  const validate = () => {
    const newErrors = {};
    
    if (frequency === 'weekly' && weeklyDays?.length === 0) {
      newErrors.weeklyDays = 'Select at least one day of the week';
    }
    
    if (frequency === 'monthly') {
      const day = parseInt(monthlyDay, 10);
      if (isNaN(day) || day < 1 || day > 31) {
        newErrors.monthlyDay = 'Enter a valid day (1-31)';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    
    const recurrenceData = {
      frequency,
      endDate: endDate || null,
      pattern: {}
    };

    if (frequency === 'weekly') {
      recurrenceData.pattern.daysOfWeek = weeklyDays;
    } else if (frequency === 'monthly') {
      recurrenceData.pattern.dayOfMonth = parseInt(monthlyDay, 10);
    }

    onSave(recurrenceData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-foreground">Set Recurring Schedule</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Configure how often this task repeats
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Frequency Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Repeat Frequency *
            </label>
            <Select
              value={frequency}
              onChange={(e) => setFrequency(e?.target?.value)}
              options={frequencyOptions}
              className="w-full"
            />
          </div>

          {/* Weekly Days Selection */}
          {frequency === 'weekly' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Repeat On *
              </label>
              <div className="flex flex-wrap gap-2">
                {daysOfWeek?.map(day => (
                  <button
                    key={day?.value}
                    onClick={() => handleToggleDay(day?.value)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      weeklyDays?.includes(day?.value)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {day?.label}
                  </button>
                ))}
              </div>
              {errors?.weeklyDays && (
                <p className="text-sm text-red-500 mt-2">{errors?.weeklyDays}</p>
              )}
            </div>
          )}

          {/* Monthly Day Selection */}
          {frequency === 'monthly' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Day of Month *
              </label>
              <Input
                type="number"
                min="1"
                max="31"
                value={monthlyDay}
                onChange={(e) => setMonthlyDay(e?.target?.value)}
                placeholder="Enter day (1-31)"
                className="w-full"
              />
              {errors?.monthlyDay && (
                <p className="text-sm text-red-500 mt-2">{errors?.monthlyDay}</p>
              )}
            </div>
          )}

          {/* End Date (Optional) */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              End Date (Optional)
            </label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e?.target?.value)}
              className="w-full"
              min={new Date()?.toISOString()?.split('T')?.[0]}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Leave empty to repeat indefinitely
            </p>
          </div>

          {/* Summary */}
          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-sm font-medium text-foreground mb-2">
              <Icon name="Info" className="w-4 h-4 inline mr-1" />
              Summary
            </p>
            <p className="text-sm text-muted-foreground">
              {frequency === 'daily' && 'This task will repeat every day'}
              {frequency === 'weekly' && weeklyDays?.length > 0 && 
                `This task will repeat every ${weeklyDays?.map(d => d?.slice(0, 3))?.join(', ')}`}
              {frequency === 'weekly'&& weeklyDays?.length === 0 && 'Select days to see summary'}
              {frequency === 'monthly' && 
                `This task will repeat on day ${monthlyDay} of each month`}
              {endDate && ` until ${new Date(endDate)?.toLocaleDateString()}`}
              {!endDate && ' indefinitely'}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
          >
            <Icon name="Check" className="w-4 h-4 mr-2" />
            Save Schedule
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RecurringTaskModal;